import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import attendanceService from '../../services/attendanceService';
import locationsService from '../../services/locationsService';
import departmentsService from '../../services/departmentsService';
import shiftsService from '../../services/shiftsService';

const LiveAttendance = () => {
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(true);

  // Filters State
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedShift, setSelectedShift] = useState('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dropdown options
  const [branchOptions, setBranchOptions] = useState([{ value: 'all', label: 'All Branches / Sites' }]);
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'all', label: 'All Departments' }]);
  const [shiftOptions, setShiftOptions] = useState([{ value: 'all', label: 'All Shifts' }]);

  // Data & State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());
  const [staffData, setStaffData] = useState([]);
  const [counts, setCounts] = useState({
    in: 0,
    out: 0,
    no_punch_in: 0,
    break: 0,
    late: 0,
    early_leaving: 0,
    total: 0
  });
  const [notifications, setNotifications] = useState([]);

  const user = {
    name: "HR Administrator",
    email: "hr@facilitypro.com",
    role: "HR Manager"
  };

  // Load Dropdown Options
  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const [bRes, dRes, sRes] = await Promise.allSettled([
        locationsService.getLocations(),
        departmentsService.getDepartments(),
        shiftsService.getShifts()
      ]);

      if (bRes.status === 'fulfilled' && bRes.value?.data) {
        const bList = Array.isArray(bRes.value.data) ? bRes.value.data : (bRes.value.data.locations || []);
        const bOpts = [{ value: 'all', label: 'All Branches / Sites' }].concat(
          bList.map(b => ({ value: b.id, label: b.name || b.address || 'Branch' }))
        );
        setBranchOptions(bOpts);
      }

      if (dRes.status === 'fulfilled' && dRes.value?.data) {
        const dList = Array.isArray(dRes.value.data) ? dRes.value.data : (dRes.value.data.departments || []);
        const dOpts = [{ value: 'all', label: 'All Departments' }].concat(
          dList.map(d => ({ value: d.id || d.name, label: d.name }))
        );
        setDepartmentOptions(dOpts);
      }

      if (sRes.status === 'fulfilled' && sRes.value?.data) {
        const sList = Array.isArray(sRes.value.data) ? sRes.value.data : (sRes.value.data.shifts || []);
        const sOpts = [{ value: 'all', label: 'All Shifts' }].concat(
          sList.map(s => ({ value: s.id, label: `${s.name} (${s.start_time || ''}-${s.end_time || ''})` }))
        );
        setShiftOptions(sOpts);
      }
    } catch (err) {
      console.error('[LiveAttendance] Dropdown load error:', err);
    }
  };

  // Fetch Live Data
  const fetchLiveData = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) setRefreshing(true);
      
      const params = {};
      if (selectedBranch !== 'all') params.branch_id = selectedBranch;
      if (selectedDepartment !== 'all') params.department_id = selectedDepartment;
      if (selectedShift !== 'all') params.shift_id = selectedShift;
      if (activeStatusFilter) params.status_filter = activeStatusFilter;

      const res = await attendanceService.getLiveAttendance(params);
      if (res.success && res.data) {
        setStaffData(res.data.staff || []);
        if (res.data.counts) {
          setCounts(res.data.counts);
        }
        setLastRefreshedAt(new Date());
      } else {
        setStaffData([]);
      }
    } catch (err) {
      console.error('[LiveAttendance] Fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBranch, selectedDepartment, selectedShift, activeStatusFilter]);

  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // 30-Second Auto-Refresh Polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLiveData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  const handlePillClick = (statusKey) => {
    if (activeStatusFilter === statusKey) {
      setActiveStatusFilter(null);
    } else {
      setActiveStatusFilter(statusKey);
    }
  };

  const handleResetFilters = () => {
    setSelectedBranch('all');
    setSelectedDepartment('all');
    setSelectedShift('all');
    setActiveStatusFilter(null);
    setSearchTerm('');
  };

  // Client-side search filtering
  const filteredStaff = useMemo(() => {
    if (!staffData) return [];
    const s = searchTerm.toLowerCase().trim();
    return staffData.filter(emp =>
      !s ||
      emp.full_name.toLowerCase().includes(s) ||
      emp.employee_code.toLowerCase().includes(s) ||
      emp.department.toLowerCase().includes(s) ||
      emp.position.toLowerCase().includes(s)
    );
  }, [staffData, searchTerm]);

  // Color generator for avatar initials fallback
  const getAvatarBg = (name) => {
    const bgColors = [
      'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300',
      'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300',
      'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/40 dark:text-teal-300',
      'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300',
      'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300',
      'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return bgColors[Math.abs(hash) % bgColors.length];
  };

  const getStatusBadge = (emp) => {
    const status = emp.status;
    if (status === 'in') {
      return {
        label: 'In',
        badgeBg: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-400',
        dotColor: 'bg-emerald-500'
      };
    } else if (status === 'out') {
      return {
        label: 'Out',
        badgeBg: 'bg-gray-500/10 text-gray-600 border-gray-500/20 dark:bg-gray-500/20 dark:text-gray-400',
        dotColor: 'bg-gray-400'
      };
    } else if (status === 'on_break') {
      return {
        label: 'Break',
        badgeBg: 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-400',
        dotColor: 'bg-amber-500 animate-pulse'
      };
    } else {
      return {
        label: 'No Punch In',
        badgeBg: 'bg-rose-500/10 text-rose-600 border-rose-500/20 dark:bg-rose-500/20 dark:text-rose-400',
        dotColor: 'bg-rose-500'
      };
    }
  };

  const filterPills = [
    { key: 'in', label: 'In', count: counts.in, color: 'emerald' },
    { key: 'out', label: 'Out', count: counts.out, color: 'gray' },
    { key: 'no_punch_in', label: 'No Punch In', count: counts.no_punch_in, color: 'rose' },
    { key: 'on_break', label: 'Break', count: counts.break, color: 'amber' },
    { key: 'late', label: 'Late', count: counts.late, color: 'orange' },
    { key: 'early_leaving', label: 'Early Leaving', count: counts.early_leaving, color: 'purple' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={notifications}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 max-w-full overflow-hidden">
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                Live Attendance
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Live Polling (30s)
                </span>
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Real-time punch activity & GPS locations for today ({new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })})
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLiveData(true)}
                disabled={refreshing}
                iconName={refreshing ? 'Loader2' : 'RefreshCw'}
                className={refreshing ? 'animate-spin' : ''}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Now'}
              </Button>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Last updated: {lastRefreshedAt.toLocaleTimeString()}
              </span>
            </div>
          </div>

          {/* Top Status Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 scrollbar-none">
            {filterPills.map(pill => {
              const isActive = activeStatusFilter === pill.key;
              return (
                <button
                  key={pill.key}
                  onClick={() => handlePillClick(pill.key)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-xs font-semibold transition-all shrink-0 cursor-pointer shadow-sm ${
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary ring-2 ring-primary/30'
                      : 'bg-card text-foreground border-border hover:bg-muted/60'
                  }`}
                >
                  <span>{pill.label}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                      isActive
                        ? 'bg-primary-foreground/20 text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {pill.count}
                  </span>
                </button>
              );
            })}

            {activeStatusFilter && (
              <button
                onClick={() => setActiveStatusFilter(null)}
                className="text-xs text-rose-500 hover:underline font-medium px-2 shrink-0 flex items-center gap-1"
              >
                <Icon name="X" size={14} /> Clear Status Filter
              </button>
            )}
          </div>

          {/* Main Layout: Left Collapsible Filter Panel + Right Staff List */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Filter Panel */}
            <div className={`lg:col-span-1 space-y-4 ${isFilterPanelOpen ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-card border border-border rounded-xl p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Icon name="Filter" size={16} className="text-primary" />
                    Filters
                  </h3>
                  <button
                    onClick={handleResetFilters}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Reset All
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Branch / Site</label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Department</label>
                  <Select
                    options={departmentOptions}
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Shift</label>
                  <Select
                    options={shiftOptions}
                    value={selectedShift}
                    onChange={setSelectedShift}
                  />
                </div>
              </div>
            </div>

            {/* Right Staff Cards Area */}
            <div className="lg:col-span-3 space-y-4">
              {/* Search & Counter Bar */}
              <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="w-full sm:w-80">
                  <Input
                    type="text"
                    placeholder="Search Staff by name, code, dept..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon="Search"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                    className="lg:hidden text-xs font-medium px-3 py-1.5 border border-border rounded-lg bg-card flex items-center gap-1.5"
                  >
                    <Icon name="Filter" size={14} />
                    {isFilterPanelOpen ? 'Hide Filters' : 'Show Filters'}
                  </button>

                  <span className="text-xs font-bold text-muted-foreground px-3 py-1.5 bg-muted rounded-lg border border-border">
                    Showing <span className="text-primary">{filteredStaff.length}</span> staff
                  </span>
                </div>
              </div>

              {/* Staff Cards List */}
              {loading ? (
                <div className="bg-card border border-border rounded-xl p-16 text-center shadow-sm">
                  <Icon name="Loader2" size={36} className="animate-spin text-primary mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm font-medium">Fetching real-time punch status...</p>
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
                  <Icon name="UserX" size={40} className="text-muted-foreground mx-auto mb-3" />
                  <h3 className="text-base font-bold text-foreground mb-1">No Staff Found</h3>
                  <p className="text-xs text-muted-foreground">
                    No active staff match the selected branch, department, shift, or search filter.
                  </p>
                  <Button onClick={handleResetFilters} variant="outline" size="sm" className="mt-4">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredStaff.map(emp => {
                    const badge = getStatusBadge(emp);
                    const avatarStyle = getAvatarBg(emp.full_name);

                    return (
                      <div
                        key={emp.id}
                        className="bg-card border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between gap-4"
                      >
                        {/* Avatar & Staff Details */}
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="relative shrink-0">
                            {emp.photo_url ? (
                              <img
                                src={emp.photo_url}
                                alt={emp.full_name}
                                className="w-11 h-11 rounded-full object-cover border border-border"
                              />
                            ) : (
                              <div
                                className={`w-11 h-11 rounded-full font-bold text-sm flex items-center justify-center border ${avatarStyle}`}
                              >
                                {emp.full_name.substring(0, 2).toUpperCase()}
                              </div>
                            )}

                            {/* Status Indicator Dot */}
                            <span
                              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${badge.dotColor}`}
                            />
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm font-bold text-foreground truncate">{emp.full_name}</h4>
                              <span className="text-[11px] font-mono text-muted-foreground font-semibold">
                                {emp.employee_code || 'EMP'}
                              </span>
                              <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded border border-border/50">
                                {emp.department}
                              </span>
                            </div>

                            {/* Location Address (Omit if no location recorded) */}
                            {emp.location_address ? (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate flex items-center gap-1 max-w-md">
                                <Icon name="MapPin" size={13} className="text-primary shrink-0" />
                                <span className="truncate">{emp.location_address}</span>
                              </p>
                            ) : (
                              <p className="text-[11px] text-muted-foreground/60 italic mt-0.5">
                                Position: {emp.position || 'Staff'} • {emp.shift_name}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Right Status Badge & Timestamp */}
                        <div className="text-right shrink-0 flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1.5 flex-wrap justify-end">
                            {emp.is_late && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                                Late
                              </span>
                            )}
                            {emp.is_early_leaving && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/10 text-purple-600 border border-purple-500/20">
                                Early Exit
                              </span>
                            )}
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-bold border ${badge.badgeBg}`}
                            >
                              {badge.label}
                            </span>
                          </div>

                          {/* Last Punch Timestamp */}
                          {emp.last_punch_time && (
                            <span className="text-[11px] font-mono text-muted-foreground font-semibold flex items-center gap-1">
                              <Icon name="Clock" size={12} />
                              {emp.last_punch_time}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <NotificationToast
        notifications={notifications}
        onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
        position="top-right"
      />
    </div>
  );
};

export default LiveAttendance;
